import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Server, Cpu, HardDrive, Shield, Cog, Database, Network, Wrench,
  Activity, Lock, Zap, Bell, RefreshCw, Workflow, Layers, Gauge,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "bare-metal": {
    eyebrow: "Dedicated · Servers",
    title: "Bare Metal Servers",
    sub: "Single-tenant hardware, full root, zero virtualization overhead.",
    metaDesc: "Bare metal dedicated servers with AMD EPYC / Intel Xeon, NVMe RAID, unmetered bandwidth and IPMI access.",
    intro:
      "Run your workload directly on physical hardware — no hypervisor, no shared resources. Bare metal is the right choice for high-IO databases, latency-sensitive trading, game servers and anything that needs every clock cycle.",
    features: [
      { icon: Cpu, title: "Latest-gen CPUs", description: "AMD EPYC 9004 and Intel Xeon Scalable Gen5." },
      { icon: HardDrive, title: "NVMe RAID", description: "Up to 8× NVMe drives in RAID 0/1/10." },
      { icon: Network, title: "Unmetered bandwidth", description: "1–100 Gbps uplinks with premium global transit." },
      { icon: Lock, title: "IPMI / KVM", description: "Out-of-band console and remote power control." },
      { icon: Shield, title: "Hardware isolation", description: "Single-tenant — no noisy neighbors, ever." },
      { icon: Zap, title: "Fast provisioning", description: "Stock SKUs ready in 2–4 hours." },
    ],
    process: [
      { step: "01", title: "Spec", description: "CPU, RAM, drives, NIC, region." },
      { step: "02", title: "Build", description: "Hardware assembled and burned-in." },
      { step: "03", title: "Deliver", description: "OS install, network, IPMI handover." },
      { step: "04", title: "Operate", description: "Monitoring + remote hands on call." },
    ],
    deliverables: ["Custom-built server", "OS preinstalled", "IPMI access", "Private VLAN", "Burn-in report"],
    techStack: ["AMD EPYC", "Intel Xeon", "NVMe RAID", "IPMI", "Ubuntu / RHEL / Proxmox"],
    pricing: [
      { name: "Entry", price: "$120", period: "month", features: ["8 cores / 32 GB", "2× 1 TB NVMe", "10 TB transfer"] },
      { name: "Performance", price: "$320", period: "month", highlighted: true, features: ["32 cores / 128 GB", "4× 2 TB NVMe", "10 Gbps uplink"] },
      { name: "Custom", price: "Custom", features: ["1 TB+ RAM", "Multi-CPU", "100 Gbps NIC"] },
    ],
    faqs: [
      { q: "Can I install ESXi or Proxmox?", a: "Yes — bring your own hypervisor, or we'll preinstall it for you." },
      { q: "How fast are network speeds?", a: "1 Gbps standard; 10/25/100 Gbps available on request." },
    ],
  },
  "gpu-servers": {
    eyebrow: "Dedicated · Servers",
    title: "GPU Servers",
    sub: "NVIDIA A100, H100 and L40S for AI, ML and rendering.",
    metaDesc: "Dedicated GPU servers with NVIDIA A100/H100/L40S for training, inference, rendering and HPC workloads.",
    intro:
      "Bare metal servers loaded with NVIDIA datacenter GPUs — from a single L40S for inference to 8× H100 SXM5 nodes with NVLink for large-model training. NVMe scratch, InfiniBand fabric and ready-to-run CUDA / PyTorch images.",
    features: [
      { icon: Cpu, title: "A100 / H100 / L40S", description: "Datacenter-grade NVIDIA GPUs, single or multi-GPU." },
      { icon: Network, title: "InfiniBand fabric", description: "200/400 Gbps RDMA for multi-node training." },
      { icon: HardDrive, title: "NVMe scratch", description: "Local NVMe for hot datasets and checkpoints." },
      { icon: Workflow, title: "ML-ready images", description: "CUDA, PyTorch, JAX, vLLM preinstalled." },
      { icon: Layers, title: "Multi-tenant Slurm", description: "Optional Slurm scheduler for shared clusters." },
      { icon: Bell, title: "GPU monitoring", description: "Per-GPU utilization, temp and ECC dashboards." },
    ],
    process: [
      { step: "01", title: "Workload sizing", description: "Model size, batch, throughput targets." },
      { step: "02", title: "Provision", description: "Single node or multi-node fabric." },
      { step: "03", title: "Stack", description: "Drivers, CUDA, frameworks, schedulers." },
      { step: "04", title: "Operate", description: "Monitoring, scaling, scheduled jobs." },
    ],
    deliverables: ["GPU node(s)", "InfiniBand fabric", "ML environment", "Monitoring", "Scheduler (optional)"],
    techStack: ["NVIDIA H100/A100/L40S", "CUDA 12", "NCCL", "InfiniBand", "Slurm", "PyTorch", "vLLM"],
    pricing: [
      { name: "Inference", price: "$1.2/hr", features: ["1× L40S 48GB", "32 vCPU / 128 GB RAM", "NVMe scratch"] },
      { name: "Training", price: "$4/hr", highlighted: true, features: ["1× H100 80GB", "64 vCPU / 256 GB RAM", "InfiniBand-ready"] },
      { name: "Cluster", price: "Custom", features: ["8× H100 SXM5", "NVLink + InfiniBand", "Multi-node Slurm"] },
    ],
    faqs: [
      { q: "H100 availability?", a: "Reserved capacity available with 2–4 week lead time; spot inventory varies." },
      { q: "Can I bring my own AMI / image?", a: "Yes — custom images, container registries and IaC all supported." },
    ],
  },
  "storage-servers": {
    eyebrow: "Dedicated · Servers",
    title: "Storage Servers",
    sub: "High-density storage for backups, archives and media.",
    metaDesc: "Dedicated storage servers up to 1 PB with HDD, SSD or hybrid pools — for backups, media and big-data workloads.",
    intro:
      "Petabyte-scale storage on dedicated hardware. Choose HDD for cold archives, SSD for hot data or hybrid pools with auto-tiering — exposed as NFS, SMB, S3 or iSCSI.",
    features: [
      { icon: HardDrive, title: "Up to 1 PB", description: "12–36 drive bays per chassis with hot-swap." },
      { icon: Layers, title: "Hybrid tiering", description: "NVMe cache + HDD bulk with auto-tiering." },
      { icon: Network, title: "Multi-protocol", description: "NFS, SMB, S3, iSCSI on the same pool." },
      { icon: Shield, title: "Resilient", description: "RAID 6, ZFS, erasure coding options." },
      { icon: RefreshCw, title: "Snapshots + replication", description: "Per-volume snapshots and async replication." },
      { icon: Lock, title: "Encryption", description: "At-rest AES-256 and in-transit TLS." },
    ],
    process: [
      { step: "01", title: "Size", description: "Capacity, IOPS, throughput targets." },
      { step: "02", title: "Build", description: "Chassis, drives, controllers." },
      { step: "03", title: "Expose", description: "Protocols, shares, ACLs." },
      { step: "04", title: "Operate", description: "Monitoring, snapshots, drive swaps." },
    ],
    deliverables: ["Storage chassis", "Pool + shares", "Replication setup", "Backup policy", "Monitoring"],
    techStack: ["ZFS", "Ceph", "MinIO (S3)", "NFS / SMB", "Enterprise HDD/SSD"],
    pricing: [
      { name: "Backup", price: "$220", period: "month", features: ["48 TB HDD", "1 Gbps NIC", "NFS / S3"] },
      { name: "Media", price: "$520", period: "month", highlighted: true, features: ["120 TB HDD + 4 TB NVMe cache", "10 Gbps NIC", "Multi-protocol"] },
      { name: "Petabyte", price: "Custom", features: ["1 PB+", "Erasure coding", "Multi-site replication"] },
    ],
    faqs: [
      { q: "Can I use it as S3?", a: "Yes — MinIO or Ceph RGW exposed as S3-compatible endpoints." },
      { q: "How are drive failures handled?", a: "RAID/ZFS auto-rebuilds and remote hands swaps within 4 hours." },
    ],
  },
  "ddos-protection": {
    eyebrow: "Dedicated · Add-ons",
    title: "DDoS Protection",
    sub: "Always-on L3/L4/L7 DDoS mitigation up to multi-Tbps.",
    metaDesc: "DDoS protection for dedicated servers: always-on L3/L4 scrubbing and L7 WAF with multi-Tbps capacity.",
    intro:
      "Attacks never sleep. Our DDoS protection scrubs traffic at the network edge — volumetric, protocol and application-layer — before it touches your server. Always-on, no human in the loop, with multi-Tbps capacity across our global scrubbing fabric.",
    features: [
      { icon: Shield, title: "Always-on", description: "Inline mitigation — no detection delay." },
      { icon: Network, title: "Multi-Tbps capacity", description: "Global anycast scrubbing centers." },
      { icon: Activity, title: "L3 / L4 / L7", description: "Volumetric, protocol and application attacks." },
      { icon: Workflow, title: "Custom rules", description: "Allowlists, geo blocks, rate limits per service." },
      { icon: Bell, title: "Real-time alerts", description: "Slack / email alerts the moment attacks start." },
      { icon: Gauge, title: "Latency-neutral", description: "Sub-1ms added latency in the steady state." },
    ],
    process: [
      { step: "01", title: "Onboard", description: "Route prefixes via GRE or direct port." },
      { step: "02", title: "Baseline", description: "Learn normal traffic profile." },
      { step: "03", title: "Protect", description: "Always-on mitigation engaged." },
      { step: "04", title: "Tune", description: "Custom rules + post-attack reports." },
    ],
    deliverables: ["Anycast scrubbing", "Custom mitigation rules", "Attack reports", "Alerting", "Quarterly review"],
    techStack: ["Anycast", "FastNetMon", "Arbor", "Custom WAF rules"],
    pricing: [
      { name: "Standard", price: "Included", features: ["L3/L4 up to 10 Gbps", "Auto-mitigation", "Email alerts"] },
      { name: "Pro", price: "$200", period: "month", highlighted: true, features: ["L7 WAF", "Custom rules", "Slack alerts"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-Tbps", "Dedicated SOC", "24/7 hotline"] },
    ],
    faqs: [
      { q: "What's the largest attack mitigated?", a: "Multi-Tbps volumetric and multi-Mrps L7 floods are routinely absorbed without customer impact." },
      { q: "Does it work for non-HTTP?", a: "Yes — game traffic (UDP), VoIP, SSH, custom protocols are all in scope." },
    ],
  },
  "server-management": {
    eyebrow: "Dedicated · Add-ons",
    title: "Server Management",
    sub: "OS patching, hardening, monitoring and incident response.",
    metaDesc: "Managed services for dedicated servers: OS patching, security hardening, monitoring, backups and 24/7 response.",
    intro:
      "Skip the sysadmin overhead. Our managed service team takes care of OS patching, security hardening, monitoring, backups and 24/7 incident response on your dedicated server — so your engineers focus on the app, not the box.",
    features: [
      { icon: Cog, title: "OS patching", description: "Weekly kernel + package updates with rollback." },
      { icon: Shield, title: "Hardening", description: "Firewall, SSH lockdown, fail2ban, audit logs." },
      { icon: Activity, title: "24/7 monitoring", description: "Uptime, CPU, RAM, disk, app health checks." },
      { icon: HardDrive, title: "Managed backups", description: "Daily backups with off-site retention." },
      { icon: Bell, title: "Incident response", description: "Engineer paged on alert with 15-min SLA." },
      { icon: Wrench, title: "Remote hands", description: "Datacenter techs for hardware tasks." },
    ],
    process: [
      { step: "01", title: "Onboard", description: "Access handover, baseline audit." },
      { step: "02", title: "Harden", description: "Firewall, SSH, audit, patch baseline." },
      { step: "03", title: "Monitor", description: "Agents installed, dashboards live." },
      { step: "04", title: "Respond", description: "Weekly reports + incident response." },
    ],
    deliverables: ["Audit report", "Hardened OS", "Monitoring stack", "Backup policy", "Monthly review"],
    techStack: ["Ansible", "Prometheus", "Grafana", "Loki", "PagerDuty", "Restic"],
    pricing: [
      { name: "Basic", price: "$80", period: "month", features: ["Patching", "Monitoring", "Business-hours support"] },
      { name: "Pro", price: "$200", period: "month", highlighted: true, features: ["Hardening", "Backups", "24/7 response"] },
      { name: "Enterprise", price: "Custom", features: ["App-level support", "Dedicated DevOps", "Quarterly reviews"] },
    ],
    faqs: [
      { q: "Do you manage apps too?", a: "Yes — common stacks (Nginx, WP, Node, Postgres, Docker, K8s) are included; bespoke apps on Pro+." },
      { q: "Will you respond to my alerts?", a: "Yes — 15-min response on critical alerts via PagerDuty integration." },
    ],
  },
};

export const Route = createFileRoute("/hosting/dedicated-servers/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/hosting/dedicated-servers/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — Dedicated Servers | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — Dedicated Servers | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: DedicatedChild,
});

function DedicatedChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
